import {naturalSort} from '@benchling/js-utils';
import {basename, extname} from 'path';
import React, {useState} from 'react';

import {CHEMICAL_EXTENSIONS, EXPORT_DISPLAY_NAME, REACTION_EXTENSIONS} from 'chemistry/ChemistryConstants';
import {MarvinChemicalFormat} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import {molConvert} from 'chemistry/molecules/MoleculeDAO';
import {ExternalFile} from 'notebook/util/NoteUtilConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';

import {chemicalStructureFromFile, downloadChemicalStructure} from './MoleculeUtil';

export interface DownloadChemicalModalProps {
  defaultFormat: MarvinChemicalFormat;
  filename: string;
  externalFile: ExternalFile;
  reaction: boolean;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export default function DownloadChemicalModal({
  defaultFormat,
  filename,
  externalFile,
  reaction,
  onRequestClose,
  onRequestSubmit,
}: DownloadChemicalModalProps): JSX.Element {
  let chemicalFileExtensions = reaction ? REACTION_EXTENSIONS : CHEMICAL_EXTENSIONS.concat(['sdf']);
  chemicalFileExtensions = naturalSort(chemicalFileExtensions);

  const [downloadFormat, setDownloadFormat] = useState<MarvinChemicalFormat>(defaultFormat);

  const downloadFormatOptions = chemicalFileExtensions.map((item, index) => (
    <Option
      value={item}
      key={index}
      onSelect={(value: MarvinChemicalFormat) => {
        setDownloadFormat(value);
      }}
    >
      {EXPORT_DISPLAY_NAME[item]}
    </Option>
  ));

  async function handleDownload(
    filename: string,
    externalFile: ExternalFile,
    defaultFormat: MarvinChemicalFormat
  ): Promise<void> {
    try {
      const chemicalStructure = await chemicalStructureFromFile(externalFile, defaultFormat);
      const convertedOutput = await molConvert(defaultFormat, downloadFormat, chemicalStructure);
      const exportedStructure = convertedOutput.structure || convertedOutput.binaryStructure!;
      const downloadFilename = `${basename(filename, extname(filename))}.${downloadFormat}`;
      downloadChemicalStructure(exportedStructure, downloadFilename, downloadFormat);
      onRequestSubmit();
    } catch (err) {
      throw new Error('Unable to download structure.');
    }
  }

  return (
    <Modal
      title="Download chemical file"
      onRequestSubmit={() => handleDownload(filename, externalFile, defaultFormat)}
      onRequestClose={onRequestClose}
      buttons={[{text: 'Download', submit: true}]}
    >
      <p>
        You're about to download the following chemical file: <b>{filename}</b>. Please select a file format
        to continue.
      </p>
      <FieldLabel label="Format" isRequired={true} />
      <FormGroup>
        <SelectDropdown
          menu={
            <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL} value={downloadFormat}>
              {downloadFormatOptions}
            </Menu>
          }
          displayName={EXPORT_DISPLAY_NAME[downloadFormat]}
        />
      </FormGroup>
    </Modal>
  );
}
